import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import { detailedServicesData } from '../../mocks/detailed-services';
import { servicesData } from '../../mocks/services';

const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');

  const categories = [
    { id: 'all', label: 'Tümü' },
    { id: 'buy-sell', label: 'Alım-Satım' },
    { id: 'repair', label: 'Tamir' },
    { id: 'software', label: 'Yazılım' },
    { id: 'corporate', label: 'Kurumsal' },
  ];

  const getCategoryServices = () => {
    if (activeCategory === 'all') return detailedServicesData;
    if (activeCategory === 'buy-sell') return detailedServicesData.filter(s => s.id <= 2);
    if (activeCategory === 'repair') return detailedServicesData.filter(s => s.id >= 3 && s.id <= 8);
    if (activeCategory === 'software') return detailedServicesData.filter(s => s.id === 5);
    if (activeCategory === 'corporate') return detailedServicesData.filter(s => s.id === 9);
    return detailedServicesData;
  };

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    
    // Set page title and meta tags
    document.title = 'Hizmetlerimiz - 2. El Bilgisayar Alım Satım ve Teknik Servis | Es Donanım Bilgisayar';
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', '2. el bilgisayar alım satım, ikinci el laptop satışı, bilgisayar tamiri, laptop tamiri, masaüstü bilgisayar tamiri, format atma, ekran değişimi ve teknik servis hizmetleri. Ankara\'da profesyonel çözümler.');
    }
    
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', '2. el bilgisayar alışı, 2. el bilgisayar satışı, ikinci el laptop, bilgisayar tamiri, laptop tamiri, masaüstü bilgisayar tamiri, format atma, ekran değişimi, fan temizliği, SSD yükseltme, RAM yükseltme, teknik servis, bilgisayar bakım');
    }
    
    // Set canonical URL
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${siteUrl}/hizmetler`);
    
    // Add Schema.org JSON-LD
    const schemaScript = document.createElement('script');
    schemaScript.type = 'application/ld+json';
    schemaScript.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ItemList",
      "itemListElement": servicesData.map((service, index) => ({
        "@type": "ListItem",
        "position": index + 1,
        "item": {
          "@type": "Service",
          "name": service.title,
          "description": service.description,
          "provider": {
            "@type": "LocalBusiness",
            "name": "Es Donanım Bilgisayar",
            "telephone": "+905065376985",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Anafartalar Cad. Ulus Şehir Çarşısı No:3/11",
              "addressLocality": "Altındağ",
              "addressRegion": "Ankara",
              "addressCountry": "TR"
            }
          },
          "areaServed": {
            "@type": "City",
            "name": "Ankara"
          }
        }
      }))
    });
    document.head.appendChild(schemaScript);
    
    return () => {
      document.head.removeChild(schemaScript);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="pb-16 lg:pb-0">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20 bg-gradient-to-br from-primary via-primary-light to-primary">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 text-center">
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 font-heading">
              Hizmetlerimiz
            </h1>
            <p className="text-xl text-white/90 max-w-3xl mx-auto">
              2. El Bilgisayar Alım Satım, Bilgisayar Tamiri, Laptop Tamiri ve Teknik Servis Hizmetleri
            </p>
          </div>
        </section>

        {/* Filter Section */}
        <section className="sticky top-20 z-30 bg-white border-b border-neutral-200 shadow-sm">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 py-6">
            <div className="flex items-center justify-center space-x-3 overflow-x-auto">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === category.id
                      ? 'bg-secondary text-white'
                      : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Services Grid */}
        <section className="py-24 bg-neutral-50">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {getCategoryServices().map((service) => (
                <div
                  key={service.id}
                  className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 hover:-translate-y-2"
                >
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 flex items-center justify-center bg-orange-500 rounded-xl shadow-lg">
                        <i className={`${service.icon} text-3xl text-white`}></i>
                      </div>
                    </div>
                  </div>

                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                      {service.shortDesc}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-6">
                      {service.features.slice(0, 3).map((feature, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                        >
                          <i className="ri-checkbox-circle-line text-orange-500"></i>
                          {feature}
                        </span>
                      ))}
                    </div>

                    <div className="flex gap-3">
                      <Link
                        to={`/hizmetler/${service.slug}`}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-semibold whitespace-nowrap"
                      >
                        <span>Detaylı Bilgi Al</span>
                        <i className="ri-arrow-right-line"></i>
                      </Link>
                      <a
                        href="https://wa.me/905551234567"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors whitespace-nowrap"
                      >
                        <i className="ri-whatsapp-line text-xl"></i>
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-24 bg-gradient-to-br from-primary via-primary-light to-primary">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6 font-heading">
              Hizmetlerimiz Hakkında Daha Fazla Bilgi Alın
            </h2>
            <p className="text-xl text-white/90 mb-10 max-w-3xl mx-auto">
              2. el bilgisayar, laptop tamiri ve teknik servis için hemen iletişime geçin
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-4">
              <a
                href="tel:+905065376985"
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-secondary hover:bg-secondary-dark text-white rounded-lg font-semibold text-base shadow-lg hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line text-xl"></i>
                <span>Hemen Ara</span>
              </a>
              <a
                href="https://wa.me/905065376985"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center space-x-3 px-8 py-4 bg-whatsapp hover:bg-whatsapp/90 text-white rounded-lg font-semibold text-base shadow-lg hover:shadow-xl transition-all whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-xl"></i>
                <span>WhatsApp ile İletişim</span>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ServicesPage;